// src/pages/black-friday-settings.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser, UserButton } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useBFAccounts } from "@/contexts/black-friday-account-context";
import { BFOutdatedScriptAlert } from "@/components/black-friday/BFOutdatedScriptAlert";
import { REQUIRED_BF_SCRIPT_VERSION } from "@/lib/black-friday-constants";
import { fetchAllBFData, fetchBFDashboardInfo } from "@/lib/black-friday-data-utils";
import { handleCopyBFScript } from "@/lib/black-friday-script-template";
import { ArrowLeft, Trash2, Plus, ExternalLink, AlertTriangle, Pencil, Copy, Loader2, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";

const TEMPLATE_URL = "https://docs.google.com/spreadsheets/d/14icbfYptNgi5FcWbHX0vVpSGB8_Cc6RtT-fqaUtipbk/copy";

export default function BlackFridaySettingsPage() {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { accounts, removeAccount, addAccount, updateAccount } = useBFAccounts();
  const { toast } = useToast();
  const [isRefreshingVersions, setIsRefreshingVersions] = useState(false);

  // Debug: Track renders and account changes
  useEffect(() => {
    console.log('[BF Settings] Component rendered. Accounts:', accounts.length);
    console.log('[BF Settings] Account list:', accounts.map(a => ({ id: a.id, name: a.accountName })));
  }, [accounts]);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [setupState, setSetupState] = useState({
    sheetUrl: "",
    webAppUrl: "",
    currentStep: 1,
    scriptCopied: false,
  });
  const [isAdding, setIsAdding] = useState(false);

  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [editAccountName, setEditAccountName] = useState("");
  const [editWebAppUrl, setEditWebAppUrl] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  // Copy script helper
  const copyScript = async () => {
    try {
      const success = await handleCopyBFScript(setupState.sheetUrl);
      if (success) {
        setSetupState((prev) => ({ ...prev, scriptCopied: true }));
        toast({
          title: "Script Copied",
          description: "The Black Friday script has been copied to your clipboard",
        });
      } else {
        throw new Error("Failed to copy");
      }
    } catch (err) {
      console.error("Failed to copy script:", err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to copy script",
      });
    }
  };

  // Reset setup state when dialog closes
  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
    setSetupState({
      sheetUrl: "",
      webAppUrl: "",
      currentStep: 1,
      scriptCopied: false,
    });
    setIsAdding(false);
  };

  // Refresh script versions for all accounts
  const refreshScriptVersions = async () => {
    if (accounts.length === 0) return;

    setIsRefreshingVersions(true);
    try {
      // Fetch dashboard info for each account in parallel
      const updates = await Promise.all(
        accounts.map(async (account) => {
          try {
            const dashboardInfo = await fetchBFDashboardInfo(account.webAppUrl);
            return {
              accountId: account.id,
              scriptVersion: dashboardInfo.scriptVersion,
              accountName: dashboardInfo.accountName,
            };
          } catch (err) {
            console.error(`Failed to fetch version for ${account.accountName}:`, err);
            return null;
          }
        })
      );

      // Update accounts with new script versions
      for (const update of updates) {
        if (update && update.scriptVersion) {
          await updateAccount(update.accountId, {
            scriptVersion: update.scriptVersion,
          });
        }
      }

      toast({
        title: "Script Versions Updated",
        description: "All account script versions have been refreshed",
      });
    } catch (err) {
      console.error("Failed to refresh script versions:", err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to refresh script versions",
      });
    } finally {
      setIsRefreshingVersions(false);
    }
  };

  // Auto-refresh script versions on page load if we have accounts without versions
  useEffect(() => {
    const needsRefresh = accounts.some(acc => !acc.scriptVersion);
    if (needsRefresh && accounts.length > 0) {
      refreshScriptVersions();
    }
  }, []); // Only run once on mount

  const handleAddAccount = async () => {
    if (!setupState.webAppUrl.trim()) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please enter your Web App URL",
      });
      return;
    }

    setIsAdding(true);
    try {
      // Fetch dashboard info to validate URL and get account details (faster than fetching all data)
      const dashboardInfo = await fetchBFDashboardInfo(setupState.webAppUrl.trim());

      // Extract account details from dashboard
      const accountCID = dashboardInfo.accountCID || "";
      const accountName = dashboardInfo.accountName || "Unknown Account";
      const scriptVersion = dashboardInfo.scriptVersion || "";

      if (!accountCID) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Could not retrieve account Customer ID from the sheet. Please ensure your script has run at least once.",
        });
        setIsAdding(false);
        return;
      }

      // Check if this account already exists
      const existingAccount = accounts.find(
        (acc) =>
          (acc.accountCID && acc.accountCID === accountCID) ||
          acc.webAppUrl === setupState.webAppUrl.trim()
      );

      if (existingAccount) {
        toast({
          variant: "destructive",
          title: "Account Already Exists",
          description: `"${existingAccount.accountName}" (CID: ${accountCID}) is already connected.`,
        });
        setIsAdding(false);
        return;
      }

      // Add account with extracted CID and name
      await addAccount(accountCID, accountName, setupState.webAppUrl.trim());

      // Update with script version if available
      if (scriptVersion) {
        await updateAccount(accountCID, { scriptVersion });
      }

      toast({
        title: "Account Added",
        description: `${accountName} has been added successfully`,
      });
      handleCloseAddDialog();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add account",
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveAccount = async (accountId: string, accountName: string) => {
    if (confirm(`Are you sure you want to remove "${accountName}"?`)) {
      try {
        await removeAccount(accountId);
        toast({
          title: "Account Removed",
          description: `${accountName} has been removed`,
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to remove account",
        });
      }
    }
  };

  const handleOpenEdit = (account: any) => {
    setEditingAccount(account);
    setEditAccountName(account.accountName);
    setEditWebAppUrl(account.webAppUrl);
    setShowEditDialog(true);
  };

  const handleEditAccount = async () => {
    if (!editAccountName.trim() || !editWebAppUrl.trim()) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all fields",
      });
      return;
    }

    setIsEditing(true);
    try {
      await updateAccount(editingAccount.id, {
        accountName: editAccountName.trim(),
        webAppUrl: editWebAppUrl.trim(),
      });
      toast({
        title: "Account Updated",
        description: `${editAccountName} has been updated successfully`,
      });
      setShowEditDialog(false);
      setEditingAccount(null);
      setEditAccountName("");
      setEditWebAppUrl("");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update account",
      });
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-muted">
      {/* Header */}
      <div className="container max-w-4xl mx-auto py-6 px-4">
        <div className="flex items-start justify-between mb-6">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/bf")}
              className="mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold">Black Friday Settings</h1>
            <p className="text-muted-foreground mt-1">
              Manage your Black Friday tracking accounts
            </p>
          </div>

          {/* User Menu */}
          {isSignedIn && user ? (
            <div className="relative z-9999">
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: "w-8 h-8",
                  },
                }}
              />
            </div>
          ) : null}
        </div>

        {/* Outdated Script Alert */}
        <BFOutdatedScriptAlert
          accounts={accounts}
          requiredScriptVersion={REQUIRED_BF_SCRIPT_VERSION}
        />

        {/* Accounts List */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Your Black Friday Accounts</CardTitle>
                <CardDescription>
                  Accounts configured with Black Friday tracking
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {accounts.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshScriptVersions}
                    disabled={isRefreshingVersions}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshingVersions ? 'animate-spin' : ''}`} />
                    {isRefreshingVersions ? "Checking..." : "Refresh Versions"}
                  </Button>
                )}
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Account
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {accounts.length === 0 ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  No Black Friday accounts configured yet. Click "Add Account" to get started.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4">
                {accounts.map((account) => (
                  <Card key={account.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{account.accountName}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            {account.accountCID && (
                              <p className="text-sm text-muted-foreground">
                                Customer ID: {account.accountCID}
                              </p>
                            )}
                            {account.scriptVersion ? (
                              <p className="text-sm text-muted-foreground">
                                • Script v{account.scriptVersion}
                              </p>
                            ) : (
                              <p className="text-sm text-amber-600 dark:text-amber-400">
                                • Script version unknown
                              </p>
                            )}
                          </div>
                          <div className="mt-2">
                            <Label className="text-xs text-muted-foreground">Web App URL:</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input
                                value={account.webAppUrl}
                                readOnly
                                className="font-mono text-xs bg-muted"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(account.webAppUrl, "_blank")}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          {account.lastUpdated && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Last updated: {new Date(account.lastUpdated).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(account)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveAccount(account.id, account.accountName)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card>
          <CardHeader>
            <CardTitle>How to Set Up Black Friday Tracking</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Step 1: Run the Script</h4>
              <p className="text-sm text-muted-foreground">
                Go to the <Button variant="link" asChild className="p-0 h-auto"><a href="/bf/setup">Setup Page</a></Button> to copy and run the Black Friday tracking script in your Google Ads account.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Step 2: Get the Web App URL</h4>
              <p className="text-sm text-muted-foreground">
                After running the script, copy the Web App URL from the Google Sheet that was created.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Step 3: Add Account Here</h4>
              <p className="text-sm text-muted-foreground">
                Click "Add Account" above and paste the Web App URL to start tracking.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Account Dialog - Multi-step Setup */}
      <Dialog open={showAddDialog} onOpenChange={(open) => !open && handleCloseAddDialog()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Another Black Friday Account</DialogTitle>
            <DialogDescription>
              Step {setupState.currentStep} of 3: {
                setupState.currentStep === 1 ? "Create Sheet" :
                setupState.currentStep === 2 ? "Install Script" :
                "Deploy Web App"
              }
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Create Sheet */}
          {setupState.currentStep === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Create a copy of the Black Friday template sheet. Set it so 'Anyone with link' can edit it (required for the script to work). Then paste the URL below & click Continue.
              </p>
              <Button
                onClick={() => window.open(TEMPLATE_URL, "_blank")}
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Copy Black Friday Template Sheet</span>
              </Button>
              <div>
                <Label htmlFor="sheetUrl">Sheet URL</Label>
                <Input
                  id="sheetUrl"
                  value={setupState.sheetUrl}
                  onChange={(e) =>
                    setSetupState((prev) => ({
                      ...prev,
                      sheetUrl: e.target.value,
                    }))
                  }
                  placeholder="Paste your sheet URL here"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseAddDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={() => setSetupState((prev) => ({ ...prev, currentStep: 2 }))}
                  disabled={!setupState.sheetUrl.includes("sheet")}
                >
                  Continue
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Install Script */}
          {setupState.currentStep === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Copy the script (created with your sheet URL) and paste it into your Google Ads account. Authorize & run it once to verify it works. The sheet will fill with BF data for 2025, 2024, and 2023. Then schedule it to run daily.
              </p>
              <p className="text-xs text-muted-foreground italic">
                Need help with Google Ads Scripts? Visit{" "}
                <a href="https://8020agent.com/scripts" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                  8020agent.com/scripts
                </a>{" "}
                for a 14min course.
              </p>
              <Button
                onClick={copyScript}
                variant={setupState.scriptCopied ? "ghost" : "default"}
                className="w-full flex items-center justify-center gap-2"
              >
                <Copy className="h-4 w-4" />
                <span>{setupState.scriptCopied ? "Script Copied!" : "Copy Script to Clipboard"}</span>
              </Button>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setSetupState((prev) => ({ ...prev, currentStep: 1 }))}
                >
                  Back
                </Button>
                <Button
                  onClick={() => setSetupState((prev) => ({ ...prev, currentStep: 3 }))}
                  disabled={!setupState.scriptCopied}
                >
                  Continue
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 3: Deploy Web App */}
          {setupState.currentStep === 3 && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-2">To get your Web App URL:</p>
                <ol className="list-decimal list-inside space-y-0.5 text-xs ml-2">
                  <li>Open your Google Sheet → <strong>Extensions</strong> → <strong>Apps Script</strong></li>
                  <li>Click <strong>Deploy</strong> → <strong>New deployment</strong></li>
                  <li>Click gear icon ⚙️ next to "Select type" → Choose <strong>Web app</strong></li>
                  <li>Set "Execute as" to <strong>Me</strong> and "Who has access" to <strong>Anyone</strong></li>
                  <li>Click <strong>Deploy</strong> and copy the <strong>Web App URL</strong></li>
                </ol>
              </div>
              <div>
                <Label htmlFor="webAppUrl">Web App URL</Label>
                <Input
                  id="webAppUrl"
                  type="url"
                  placeholder="https://script.google.com/macros/s/..."
                  value={setupState.webAppUrl}
                  onChange={(e) =>
                    setSetupState((prev) => ({
                      ...prev,
                      webAppUrl: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isAdding) {
                      handleAddAccount();
                    }
                  }}
                  disabled={isAdding}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The account name and Customer ID will be fetched automatically from your sheet.
                </p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setSetupState((prev) => ({ ...prev, currentStep: 2 }))}
                  disabled={isAdding}
                >
                  Back
                </Button>
                <Button
                  onClick={handleAddAccount}
                  disabled={isAdding || !setupState.webAppUrl || !setupState.webAppUrl.includes("macros")}
                >
                  {isAdding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    "Add Account"
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
            <DialogDescription>
              Update account name or Web App URL
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="editAccountName">Account Name</Label>
              <Input
                id="editAccountName"
                placeholder="My Google Ads Account"
                value={editAccountName}
                onChange={(e) => setEditAccountName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="editAccountCID">Customer ID (CID)</Label>
              <Input
                id="editAccountCID"
                value={editingAccount?.accountCID || ""}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Customer ID cannot be changed
              </p>
            </div>
            <div>
              <Label htmlFor="editWebAppUrl">Web App URL</Label>
              <Input
                id="editWebAppUrl"
                placeholder="https://script.google.com/..."
                value={editWebAppUrl}
                onChange={(e) => setEditWebAppUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                The Web App URL from your Black Friday Google Sheet
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditAccount} disabled={isEditing}>
              {isEditing ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
